'use client';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './modal.module.css';

interface ModalProps {
	isOpen: boolean;
	onClose: () => void;
	children: React.ReactNode;
	auto?: boolean;
}
interface ModalComponent extends React.FC<ModalProps> {
	Title: React.FC<{ children: React.ReactNode }>;
	Body: React.FC<{ children: React.ReactNode }>;
	Footer: React.FC<{ children?: React.ReactNode }>;
}

const Modal: ModalComponent = ({ isOpen, onClose, children, auto }) => {
	const [modalRoot, setModalRoot] = useState<HTMLElement | null>(null);

	const modalRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const rootDiv = document.getElementById('modal-root');
		if (rootDiv) {
			setModalRoot(rootDiv);
		}
	}, []);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				modalRef.current &&
				!modalRef.current.contains(event.target as Node)
			) {
				onClose();
			}
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				onClose();
			}
		};

		if (isOpen && modalRoot) {
			document.addEventListener('mousedown', handleClickOutside);
			document.addEventListener('keydown', handleKeyDown);
			document.body.style.overflow = 'hidden';
		} else {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleKeyDown);
			document.body.style.overflow = '';
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleKeyDown);
			document.body.style.overflow = '';
		};
	}, [isOpen, onClose, modalRoot]);

	if (!isOpen || !modalRoot) {
		return null;
	}

	const modalContent = (
		<div className={styles.modalOverlay}>
			<div
				className={styles.modal}
				ref={modalRef}
				style={{
					width: auto ? 'auto' : '70%',
				}}
			>
				{children}
			</div>
		</div>
	);
	return createPortal(modalContent, modalRoot);
};

Modal.Title = function ({ children }: { children: React.ReactNode }) {
	return <div className={styles['modal-header']}>{children}</div>;
};

Modal.Body = function ({ children }: { children: React.ReactNode }) {
	return <div className={styles['modal-body']}>{children}</div>;
};

Modal.Footer = function ({ children }: { children?: React.ReactNode }) {
	return <div className={styles['modal-footer']}>{children}</div>;
};

export default Modal;
